import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import useResizeObserver from 'use-resize-observer';
import styled from 'styled-components';
import { IntelligentPartnerHomePageCourseScoresTrend } from 'src/api/types';
import { Spin } from 'udesk-ui';
import UdeskLocales from 'UdeskLocales';
const ChartContainer = styled.div`
    flex: 1;
    height: 400px;
`;

const LoadingDiv = styled.div`
    display: flex;
    flex: 1;
    height: 400px;
    align-items: center;
    justify-content: center;
`;

interface TrendChartProps {
    loading?: boolean;
    data?: IntelligentPartnerHomePageCourseScoresTrend[];
}

export const TrendChart: React.FC<TrendChartProps> = ({ loading = false, data = [] }) => {
    const chartRef = useRef<any>(null);
    const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);
    const { ref, width = 1, height = 1 } = useResizeObserver<HTMLDivElement>();

    useEffect(() => {
        if (!chartRef.current || loading) return;
        const chart = echarts.init(chartRef.current);
        setChartInstance(chart);

        return () => {
            chart.dispose();
        };
    }, [loading]);

    useEffect(() => {
        if (!chartInstance || loading) return;

        // 计算所有折线数据的最大值和最小值
        const allValues: number[] = [
            ...data.map((item) => item.historicalAvgScore),
            ...data.map((item) => item.historicalTotalCount),
            ...data.map((item) => item.newOverallScore),
            ...data.map((item) => item.newEvaluationCount),
        ].filter((value): value is number => value !== undefined && value !== null);

        const maxValue = Math.ceil(Math.max(...allValues));
        const minValue = Math.floor(Math.min(...allValues));
        const interval = Math.ceil((maxValue - minValue) / 5);

        const option: echarts.EChartsOption = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    label: {
                        backgroundColor: '#6a7985',
                    },
                },
            },
            legend: {
                data: [/* 综合得分 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.comprehensiveScore, /* 评价总数量 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.totalQuantityOfEvaluation, /* 新增评价综合得分 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.addedComprehensiveEvaluationScore, /* 新增评价数量 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.quantityOfNewEvaluation],
                top: 0,
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '40px',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: data.map((item) => item.startPoint),
            },
            yAxis: [
                {
                    type: 'value',
                    name: /* 得分 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.score,
                    min: minValue,
                    max: maxValue,
                    interval: interval,
                    axisLabel: {
                        formatter: '{value}',
                    },
                },
            ],
            series: [
                {
                    name: /* 综合得分 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.comprehensiveScore,
                    type: 'line',
                    smooth: true,
                    lineStyle: {
                        width: 2,
                        color: '#1890ff',
                    },
                    symbol: 'circle',
                    symbolSize: 6,
                    data: data.map((item) => item.historicalAvgScore),
                },
                {
                    name: /* 评价总数量 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.totalQuantityOfEvaluation,
                    type: 'line',
                    smooth: true,
                    lineStyle: {
                        width: 2,
                        color: '#52c41a',
                    },
                    symbol: 'circle',
                    symbolSize: 6,
                    data: data.map((item) => item.historicalTotalCount),
                },
                {
                    name: /* 新增评价综合得分 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.addedComprehensiveEvaluationScore,
                    type: 'line',
                    smooth: true,
                    lineStyle: {
                        width: 2,
                        color: '#f5222d',
                    },
                    symbol: 'circle',
                    symbolSize: 6,
                    data: data.map((item) => item.newOverallScore),
                },
                {
                    name: /* 新增评价数量 */UdeskLocales['current'].pages.coach.courseEvaluation.overview.components.trendChart.quantityOfNewEvaluation,
                    type: 'line',
                    smooth: true,
                    lineStyle: {
                        width: 2,
                        color: '#faad14',
                    },
                    symbol: 'circle',
                    symbolSize: 6,
                    data: data.map((item) => item.newEvaluationCount),
                },
            ],
        };

        chartInstance.setOption(option);
    }, [chartInstance, data, loading]);

    useEffect(() => {
        requestIdleCallback(() => {
            chartInstance?.resize?.();
        });
    }, [width, height, chartInstance]);
    if (loading) {
        return (
            <LoadingDiv>
                <Spin />
            </LoadingDiv>
        );
    }
    return (
        <ChartContainer
            ref={(el) => {
                chartRef.current = el;
                ref(el);
            }}
        />
    );
};
